// BusinessMapCountry.js — /map/:iso2 country-scoped map view.
//
// UPSTREAM API calls:
//   - GET /api/businesses?has_coordinates=true (pin layer)
//   - GET /api/events                         (event overlay)
//   Shares visibility rules with Directory.js (isGlobalScope ↔ isInCountry).
//
// DOWNSTREAM components:
//   - @/components/map/*  — shared primitives across
//     BusinessMapCountry and BusinessMapRegion.
//   - mapDataCache (IndexedDB) — warms on mount via startBackgroundPrefetch.
//
// Hook-order note: ISO2 may be missing; we still run every hook below with
// a safe fallback and defer the redirect to the return branch. See comment
// at `navigateAway` for the Rules-of-Hooks rationale.
import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import ShowRegionButton from '../components/map/ShowRegionButton';
import ExchangesPanel from '../components/map/ExchangesPanel';
import { startBackgroundPrefetch } from '../services/mapDataCache';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import '@/styles/leaflet-dark.css';
import L from 'leaflet';
import { useTheme } from '@/contexts/ThemeContext';

import {
  API,
  orangeIcon, btcmapIcon, atmIcon, eventIcon,
  QUICK_CATEGORIES,
  isATM, matchesCategoryOrTag, getMarkerCoords, isBusinessVisible,
  MapBoundsTracker, FlyToLocation, useUserLocation,
  SearchBar, CategoryPills, Sidebar, BusinessPopup, EventPopup,
  EditSuggestionDialog, MapLegend
} from '@/components/map';
import { useTileConfig, tileLayerProps } from '@/components/map/tiles';

import COUNTRIES from '@/data/countries.json';
import CATEGORIES_DATA from '@/data/categories.json';
import { isGlobalScope } from '../utils/businessScope';

// Build Leaflet bounds and state-bounds for a country ISO2 code
const getCountryBounds = (iso2) => {
  const c = COUNTRIES[iso2.toUpperCase()];
  if (!c) return null;
  const { lat_min, lat_max, lon_min, lon_max } = c.bbox;
  return L.latLngBounds(L.latLng(lat_min, lon_min), L.latLng(lat_max, lon_max));
};

// Default center/zoom per country (fallback if bbox unavailable)
const DEFAULT_VIEW = { center: [-25.2744, 133.7751], zoom: 5 };

const getCountryView = (iso2) => {
  const bounds = getCountryBounds(iso2);
  if (!bounds) return DEFAULT_VIEW;
  const center = [
    (bounds.getSouth() + bounds.getNorth()) / 2,
    (bounds.getWest() + bounds.getEast()) / 2
  ];
  return { bounds, center };
};

// Filter predicate for a business being **based in** this country.
//
// The map view answers "who's HERE?" — not "who serves this country?".
// Global-scope and country-of-service matches don't qualify: the user
// goes to /directory for that wider filter. We only include businesses
// whose home country is this one (by explicit country_code or by coord
// fallback for legacy records).
const isInCountry = (b, iso2Upper, countryBounds) => {
  // Global-scope records belong on /directory (the "who serves here?" view),
  // not the "who's HERE?" map. Exclude them via the shared predicate so this
  // page stays symmetric with the backend and BusinessMapRegion.
  if (isGlobalScope(b)) return false;
  if (b.country_code && b.country_code.toUpperCase() === iso2Upper) return true;
  const lat = b.latitude || b.lat;
  const lng = b.longitude || b.lon;
  if (lat && lng && countryBounds) {
    return countryBounds.contains(L.latLng(lat, lng));
  }
  return false;
};

const isEventInCountry = (e, iso2Upper, countryBounds) => {
  if (e.country && e.country.toUpperCase() === iso2Upper) return true;
  if (e.latitude && e.longitude && countryBounds) {
    return countryBounds.contains(L.latLng(e.latitude, e.longitude));
  }
  return false;
};

// Cluster icon factory
const makeClusterIcon = (bg) => (cluster) => L.divIcon({
  html: `<div style="background:${bg};color:white;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:13px;border:2px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);">${cluster.getChildCount()}</div>`,
  className: '',
  iconSize: L.point(36, 36, true)
});

const BusinessMapCountry = () => {
  const { iso2 } = useParams();

  // ISO 3166-1 aliases — common incorrect codes users might type into
  // URL bar. Data is stored under the canonical code, so normalize the
  // URL-derived code before any lookup. Currently handles UK→GB, but
  // the map is the natural place to add more (EL→GR, etc.).
  const ISO_ALIASES = { UK: 'GB', EL: 'GR' };
  const rawIso = (iso2 || 'AU').toUpperCase();
  const ISO2 = ISO_ALIASES[rawIso] || rawIso;

  // Don't early-return here — React's Rules of Hooks require every hook
  // below to run in the same order each render. We flag a missing OR unknown
  // ISO2 (mirrors BusinessMapRegion's slug validation) and redirect at the
  // bottom after all hooks have fired. Without the COUNTRIES check an unknown
  // code silently fell through to the AU default view.
  const navigateAway = !iso2 || !COUNTRIES[ISO2];
  const countryBounds = getCountryBounds(ISO2);
  const countryView = getCountryView(ISO2);

  // Build state-bounds object so isBusinessVisible works correctly
  const stateBoundsForCountry = useMemo(() => {
    if (!countryBounds) return {};
    return { [ISO2]: countryBounds };
  }, [ISO2, countryBounds]);

  // Core data state
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [mapEvents, setMapEvents] = useState([]);
  const [mapTags, setMapTags] = useState([{ name: 'All', categories: [] }]);
  // Category list is static (see categories.json); no admin fetch needed.
  const categories = useMemo(
    () => CATEGORIES_DATA.filter(c => c.slug).map(c => c.name).sort((a, b) => a.localeCompare(b)),
    []
  );
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [exchanges, setExchanges] = useState(null);

  // Map state
  const [mapBounds, setMapBounds] = useState(null);
  const [flyToCoords, setFlyToCoords] = useState(null);
  const [flyTrigger, setFlyTrigger] = useState(0);
  const [pinnedPopup, setPinnedPopup] = useState(null);
  const [pinnedEventId, setPinnedEventId] = useState(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showOrangeLabel, setShowOrangeLabel] = useState(true);
  const [showBtcMap, setShowBtcMap] = useState(true);
  const [showATMs, setShowATMs] = useState(false);
  const [showEvents, setShowEvents] = useState(true);

  // UI state
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);

  // Edit suggestion state
  const [editSuggestionDialog, setEditSuggestionDialog] = useState(false);
  const [editSuggestionBusiness, setEditSuggestionBusiness] = useState(null);
  const [editSuggestionForm, setEditSuggestionForm] = useState({
    website: '', contact: '', address: '', categories: [],
    submitterName: '', submitterEmail: ''
  });
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // Dark mode tile switching via ThemeContext
  const { darkMode } = useTheme();
  const tileConfig = useTileConfig();
  const tileLayer = tileLayerProps(darkMode, tileConfig);

  const { locateUser } = useUserLocation(setFlyToCoords, setFlyTrigger);

  useEffect(() => {
    setLoading(true);
    setFetchError(false);
    const fetchData = async () => {
      // Every fetch has its own .catch so one transient upstream failure
      // (e.g. a 524 on the heavy /businesses query) does not wipe out the
      // other panels — notably `exchanges`, which was getting stuck at
      // null on mobile Safari when /businesses was slow. The businesses
      // request additionally flags __error so a failed fetch shows a retry
      // banner instead of masquerading as "no listings".
      const [businessRes, tagsRes, eventsRes, mapEventsRes, exchangesRes] = await Promise.all([
        axios.get(`${API}/businesses?country_code=${ISO2}`).catch(() => ({ data: [], __error: true })),
        axios.get(`${API}/admin/map-tags`).catch(() => ({ data: { tags: [] } })),
        axios.get(`${API}/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/events?for_map=true`).catch(() => ({ data: [] })),
        axios.get(`${API}/exchanges?country=${ISO2}`).catch(() => ({ data: [] })),
      ]);

      setFetchError(!!businessRes.__error);
      const bounds = getCountryBounds(ISO2);
      setAllBusinesses((businessRes.data || []).filter(b => isInCountry(b, ISO2, bounds)));

      if (tagsRes.data?.tags?.length > 0) {
        setMapTags(tagsRes.data.tags);
      } else {
        setMapTags(QUICK_CATEGORIES.map(c => ({ name: c, categories: c === 'All' ? [] : [c] })));
      }
      setEvents((eventsRes.data || []).filter(e => isEventInCountry(e, ISO2, bounds)));
      setMapEvents((mapEventsRes.data || []).filter(e => isEventInCountry(e, ISO2, bounds)));
      setExchanges(Array.isArray(exchangesRes.data) ? exchangesRes.data : []);
      setLoading(false);

      // Kick off background prefetch for other regions once initial data is ready.
      // Determine current region slug from the country meta so prefetch knows
      // which adjacent region to fetch first.
      try {
        const COUNTRIES_DATA = (await import('@/data/countries.json')).default;
        const currentRegionSlug = COUNTRIES_DATA[ISO2]?.region || 'oceania';
        startBackgroundPrefetch(currentRegionSlug);
      } catch {
        // Non-critical — ignore failures
      }
    };
    fetchData();
  }, [ISO2, reloadKey]);

  const handleBoundsChange = useCallback((bounds) => setMapBounds(bounds), []);
  const resetFilters = useCallback(() => { setSearchQuery(''); setSelectedCategory('All'); }, []);
  const hasActiveFilters = useMemo(() => searchQuery || selectedCategory !== 'All', [searchQuery, selectedCategory]);

  const handleCardClick = useCallback((b) => {
    setSelectedBusiness(b);
    const coords = getMarkerCoords(b);
    setFlyToCoords(coords);
    setFlyTrigger(prev => prev + 1);
  }, []);

  const handleFlyToEvent = useCallback((event) => {
    if (event.latitude && event.longitude) {
      setFlyToCoords([event.latitude, event.longitude]);
      setFlyTrigger(prev => prev + 1);
    }
  }, []);

  const openEditSuggestion = useCallback((business) => {
    setEditSuggestionBusiness(business);
    setEditSuggestionForm({
      website: business.website || '',
      contact: business.contact || '',
      address: `${business.based_state || ''} ${business.region || ''}`.trim(),
      categories: business.category || [],
      submitterName: '', submitterEmail: ''
    });
    setEditSuggestionDialog(true);
  }, []);

  const submitEditSuggestion = async () => {
    if (!editSuggestionBusiness) return;
    setSubmittingSuggestion(true);
    try {
      const changes = {};
      if (editSuggestionForm.website !== (editSuggestionBusiness.website || '')) {
        changes.website = { old: editSuggestionBusiness.website || '', new: editSuggestionForm.website };
      }
      if (editSuggestionForm.contact !== (editSuggestionBusiness.contact || '')) {
        changes.contact = { old: editSuggestionBusiness.contact || '', new: editSuggestionForm.contact };
      }
      if (editSuggestionForm.address !== `${editSuggestionBusiness.based_state || ''} ${editSuggestionBusiness.region || ''}`.trim()) {
        changes.region = { old: editSuggestionBusiness.region || '', new: editSuggestionForm.address };
      }
      const origCats = (editSuggestionBusiness.category || []).sort().join(',');
      const newCats = (editSuggestionForm.categories || []).sort().join(',');
      if (origCats !== newCats) {
        changes.category = { old: editSuggestionBusiness.category || [], new: editSuggestionForm.categories };
      }
      if (Object.keys(changes).length === 0) {
        toast.error('No changes detected');
        setSubmittingSuggestion(false);
        return;
      }
      await axios.post(`${API}/suggestions`, {
        business_id: editSuggestionBusiness.id,
        changes,
        name: editSuggestionForm.submitterName,
        email: editSuggestionForm.submitterEmail
      });
      toast.success('Thank you! Your suggestion has been submitted for review.');
      setEditSuggestionDialog(false);
    } catch (e) {
      toast.error('Failed to submit suggestion');
    }
    setSubmittingSuggestion(false);
  };

  const sourceFilteredBusinesses = useMemo(() => {
    let f = [...allBusinesses];
    if (selectedCategory !== 'All') {
      f = f.filter(b => matchesCategoryOrTag(b, selectedCategory, mapTags));
    }
    const atms = f.filter(isATM);
    const nonAtms = f.filter(b => !isATM(b));
    let r = [];
    if (showOrangeLabel) r = r.concat(nonAtms.filter(b => !b.btc_map));
    if (showBtcMap) r = r.concat(nonAtms.filter(b => b.btc_map));
    if (showATMs) r = r.concat(atms);
    return r;
  }, [allBusinesses, showOrangeLabel, showBtcMap, showATMs, selectedCategory, mapTags]);

  const visibleBusinesses = useMemo(() => {
    let businesses = sourceFilteredBusinesses.filter(b => isBusinessVisible(b, mapBounds, stateBoundsForCountry));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      businesses = businesses.filter(b =>
        (b.business_name || '').toLowerCase().includes(q) ||
        (b.category || []).some(c => c.toLowerCase().includes(q)) ||
        (b.region || '').toLowerCase().includes(q)
      );
    }
    return businesses;
  }, [sourceFilteredBusinesses, mapBounds, searchQuery, stateBoundsForCountry]);

  const createOrangeCluster = useCallback(makeClusterIcon('#f97316'), []);
  const createBtcmapCluster = useCallback(makeClusterIcon('#0891B2'), []);
  const createAtmCluster = useCallback(makeClusterIcon('#8b5cf6'), []);

  const orangeLabelBusinesses = useMemo(() => sourceFilteredBusinesses.filter(b => !b.btc_map && !isATM(b)), [sourceFilteredBusinesses]);
  const btcmapBusinesses = useMemo(() => sourceFilteredBusinesses.filter(b => b.btc_map && !isATM(b)), [sourceFilteredBusinesses]);
  const atmBusinesses = useMemo(() => sourceFilteredBusinesses.filter(isATM), [sourceFilteredBusinesses]);
  const visibleMapEvents = useMemo(() => mapEvents.filter(e => e.latitude && e.longitude), [mapEvents]);

  const getEventPosition = useCallback((event) => {
    const eventLat = event.latitude;
    const eventLng = event.longitude;
    const hasOverlap = allBusinesses.some(b => {
      const bLat = b.latitude || b.lat;
      const bLng = b.longitude || b.lon;
      if (!bLat || !bLng) return false;
      return Math.abs(bLat - eventLat) < 0.0005 && Math.abs(bLng - eventLng) < 0.0005;
    });
    if (hasOverlap) return [eventLat + 0.0003, eventLng + 0.0004];
    return [eventLat, eventLng];
  }, [allBusinesses]);

  const toggleSidebar = useCallback(() => setShowSidebar(prev => !prev), []);

  const createMarkerHandlers = useCallback((b) => ({
    click: (e) => { setPinnedPopup(b.id); e.target.openPopup(); },
    mouseover: (e) => { if (pinnedPopup !== b.id) e.target.openPopup(); },
    mouseout: (e) => { if (pinnedPopup !== b.id) e.target.closePopup(); }
  }), [pinnedPopup]);

  // Guard: route-param was missing — redirect now that all hooks have run.
  if (navigateAway) {
    return <Navigate to="/map/au" replace />;
  }

  if (loading) {
    return (
      <div className="h-[calc(100dvh-64px)] flex items-center justify-center bg-muted">
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  // Map initialisation props — use fitBounds if we have a valid bbox, else fallback
  const mapProps = countryView.bounds
    ? { bounds: countryView.bounds, boundsOptions: { padding: [20, 20] } }
    : { center: countryView.center, zoom: 5 };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100dvh - 64px)' }}>
      <div className="relative flex-1 min-h-0">

        {fetchError && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-destructive text-destructive-foreground rounded-lg shadow-lg px-4 py-2 text-sm flex items-center gap-3">
            <span>Couldn&apos;t load listings — this is a connection issue, not an empty area.</span>
            <button
              onClick={() => setReloadKey(k => k + 1)}
              className="underline font-medium whitespace-nowrap hover:opacity-80"
            >
              Retry
            </button>
          </div>
        )}

        <MapContainer
          {...mapProps}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          zoomControl={false}
        >
          <ZoomControl position="bottomright" />
          <TileLayer {...tileLayer} />
          <MapBoundsTracker onBoundsChange={handleBoundsChange} />
          <FlyToLocation coords={flyToCoords} trigger={flyTrigger} />

          {showOrangeLabel && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createOrangeCluster}>
              {orangeLabelBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`ol-${b.id}`} position={c} icon={orangeIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={openEditSuggestion} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showBtcMap && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createBtcmapCluster}>
              {btcmapBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`btc-${b.id}`} position={c} icon={btcmapIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={null} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showATMs && (
            <MarkerClusterGroup chunkedLoading iconCreateFunction={createAtmCluster}>
              {atmBusinesses.map(b => {
                const c = getMarkerCoords(b);
                return c && (
                  <Marker key={`atm-${b.id}`} position={c} icon={atmIcon} eventHandlers={createMarkerHandlers(b)}>
                    <Popup closeButton={true} onClose={() => { if (pinnedPopup === b.id) setPinnedPopup(null); }}>
                      <BusinessPopup business={b} onSuggestEdit={null} />
                    </Popup>
                  </Marker>
                );
              })}
            </MarkerClusterGroup>
          )}

          {showEvents && visibleMapEvents.map(event => (
            <Marker
              key={`event-${event.id}`}
              position={getEventPosition(event)}
              icon={eventIcon}
              zIndexOffset={1000}
              eventHandlers={{
                click: (e) => {
                  if (pinnedEventId === event.id) {
                    setPinnedEventId(null);
                    e.target.closePopup();
                  } else {
                    setPinnedEventId(event.id);
                    e.target.openPopup();
                  }
                },
                mouseover: (e) => { e.target.openPopup(); },
                mouseout: (e) => { if (pinnedEventId !== event.id) e.target.closePopup(); }
              }}
            >
              <Popup
                className="event-popup"
                maxWidth={300}
                autoClose={false}
                closeOnClick={false}
                onClose={() => { if (pinnedEventId === event.id) setPinnedEventId(null); }}
              >
                <EventPopup
                  event={event}
                  isPinned={pinnedEventId === event.id}
                  onClose={() => { setPinnedEventId(null); }}
                />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          searchFocused={searchFocused}
          setSearchFocused={setSearchFocused}
          visibleBusinesses={visibleBusinesses}
          onSelectBusiness={handleCardClick}
          onToggleSidebar={toggleSidebar}
          onLocateUser={locateUser}
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />

        {/* Show-Region affordance — only renders when user has panned outside the current country */}
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[490] pointer-events-none">
          <ShowRegionButton mapBounds={mapBounds} currentIso2={ISO2} />
        </div>

        <CategoryPills
          mapTags={mapTags}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />

        <MapLegend />

        <button
          onClick={toggleSidebar}
          className="hidden md:block absolute top-4 right-4 z-[400] bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          {showSidebar ? 'Hide List' : 'Show List'}
        </button>

        <Sidebar
          showSidebar={showSidebar}
          setShowSidebar={setShowSidebar}
          visibleBusinesses={visibleBusinesses}
          events={events}
          mapBounds={mapBounds}
          showOrangeLabel={showOrangeLabel}
          setShowOrangeLabel={setShowOrangeLabel}
          showBtcMap={showBtcMap}
          setShowBtcMap={setShowBtcMap}
          showEvents={showEvents}
          setShowEvents={setShowEvents}
          showATMs={showATMs}
          setShowATMs={setShowATMs}
          selectedBusiness={selectedBusiness}
          onSelectBusiness={handleCardClick}
          onFlyToEvent={handleFlyToEvent}
          hasActiveFilters={hasActiveFilters}
          resetFilters={resetFilters}
        />

        <EditSuggestionDialog
          open={editSuggestionDialog}
          onOpenChange={setEditSuggestionDialog}
          business={editSuggestionBusiness}
          form={editSuggestionForm}
          setForm={setEditSuggestionForm}
          categories={categories}
          onSubmit={submitEditSuggestion}
          isSubmitting={submittingSuggestion}
        />
      </div>

      {/* Exchanges panel below map */}
      <ExchangesPanel exchanges={exchanges} />
    </div>
  );
};

export default BusinessMapCountry;
